import { Room } from "../../types/room";

// ===================== DATE UTILITIES =====================

export function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseTimeToHours(str: string): number {
  const [h, m] = str.split(":").map(Number);
  return h + m / 60;
}

export function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getWeekDays(monday: Date): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return formatDateStr(d);
  });
}

// ===================== PRICING LOGIC =====================

export function calculateDuration(
  checkInDate: string,
  checkInTime: string,
  checkOutDate: string,
  checkOutTime: string
): number {
  const checkIn = new Date(`${checkInDate}T${checkInTime}`);
  const checkOut = new Date(`${checkOutDate}T${checkOutTime}`);
  // Return raw float — Math.ceil applied per-use in billing calculations
  return (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60);
}

export function detectShiftType(
  checkInDate: string,
  checkInTime: string,
  checkOutDate: string,
  checkOutTime: string
): string {
  const duration = calculateDuration(checkInDate, checkInTime, checkOutDate, checkOutTime);
  const dayDiff = Math.round(
    (new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) / (1000 * 60 * 60 * 24)
  );
  const sameDay = dayDiff === 0;

  // Ca ngày: ≥ 22 tiếng (ngày đầu 22h, mỗi ngày tiếp theo 24h)
  if (duration >= 22) return "daily";

  // Ca đêm: qua ngày (dayDiff ≥ 1) và < 22 tiếng
  if (!sameDay) return "overnight";

  // Ca 9h: cùng ngày + ≥ 9h + < 22h (đúng khung 10h–19h = 9 tiếng chẵn)
  if (sameDay && duration >= 9 && duration < 22) return "daytime_shift";

  // Ca 4h: cùng ngày + < 9h
  return "normal_hourly";
}

export interface PriceResult {
  price: number;
  rateType: string;
  shiftLabel: string;
}

function isWeekendDate(dateStr: string): boolean {
  // "yyyy-mm-dd" phải dựng theo giờ địa phương: new Date("yyyy-mm-dd") là nửa đêm UTC nên ở múi giờ
  // phía tây Việt Nam (Mỹ...) getDay() ra ngày hôm trước → phân loại sai T6/T7/CN và hiện sai giá.
  const [y, m, d] = dateStr.split("-").map(Number);
  const day = new Date(y, m - 1, d).getDay(); // 0=CN, 5=T6, 6=T7
  return day === 0 || day === 5 || day === 6;
}

export function calculatePriceByShift(
  shiftType: string,
  checkInDate: string,
  checkInTime: string,
  checkOutDate: string,
  checkOutTime: string,
  room: Room
): PriceResult {
  const duration     = Math.ceil(calculateDuration(checkInDate, checkInTime, checkOutDate, checkOutTime));
  const dayDiff = Math.round(
    (new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  let result: PriceResult;

  // Giới hạn giá ca giờ thường / ca ngày không vượt quá giá theo ngày (dailyRate) —
  // ca đêm cố ý KHÔNG áp dụng luật này
  const capAtDailyRate = (r: PriceResult): PriceResult => {
    if (room.price.dailyRate && r.price > room.price.dailyRate) {
      return { ...r, price: room.price.dailyRate, rateType: `${r.rateType} (giới hạn giá ngày)` };
    }
    return r;
  };

  switch (shiftType) {
    case "normal_hourly": {
      // Tối thiểu 4h. Extra > 4h → +60k/h
      const billable = Math.max(duration, 4);
      if (billable <= 4) {
        result = capAtDailyRate({ price: room.price.pricePer4Hours, rateType: "Giá giờ thường (4h)", shiftLabel: "Ca giờ thường" });
      } else {
        const extra = billable - 4;
        result = capAtDailyRate({
          price: room.price.pricePer4Hours + extra * 60_000,
          rateType: `4h + ${extra}h × 60.000đ`,
          shiftLabel: "Ca giờ thường",
        });
      }
      break;
    }

    case "daytime_shift": {
      // Tối thiểu 9h (khung 10h–19h). Extra > 9h → +60k/h
      const billable = Math.max(duration, 9);
      if (billable <= 9) {
        result = capAtDailyRate({ price: room.price.pricePer9Hours, rateType: "Ca trong ngày (10h–19h)", shiftLabel: "Ca trong ngày" });
      } else {
        const extra = billable - 9;
        result = capAtDailyRate({
          price: room.price.pricePer9Hours + extra * 60_000,
          rateType: `Ca 9h + ${extra}h × 60.000đ`,
          shiftLabel: "Ca trong ngày",
        });
      }
      break;
    }

    case "overnight": {
      // Khung 20h–9h = 13h. Extra (in sớm / out trễ) → +60k/h
      const coreHours = 13;
      const billable  = Math.max(duration, coreHours);
      if (billable <= coreHours) {
        result = capAtDailyRate({ price: room.price.priceOvernight13Hours, rateType: "Ca đêm (20h–9h)", shiftLabel: "Ca qua đêm" });
      } else {
        const extra = billable - coreHours;
        result = capAtDailyRate({
          price: room.price.priceOvernight13Hours + extra * 60_000,
          rateType: `Ca đêm + ${extra}h × 60.000đ`,
          shiftLabel: "Ca qua đêm",
        });
      }
      break;
    }

    case "daily": {
      // Tính từng ngày riêng: T6/T7/CN → dailyRateHoliday, ngày thường → dailyRate
      let price = 0;
      let weekdayCount = 0;
      let weekendCount = 0;
      for (let i = 0; i < dayDiff; i++) {
        const d = new Date(`${checkInDate}T12:00:00`);
        d.setDate(d.getDate() + i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
        if (isWeekendDate(dateStr) && room.price.dailyRateHoliday) {
          price += room.price.dailyRateHoliday;
          weekendCount++;
        } else {
          price += room.price.dailyRate;
          weekdayCount++;
        }
      }
      const parts: string[] = [];
      if (weekdayCount > 0) parts.push(`${weekdayCount} ngày × giá ngày`);
      if (weekendCount > 0) parts.push(`${weekendCount} ngày × giá T6/T7/CN`);
      // Phụ thu tính theo TỔNG thời gian ở so với giờ nhận phòng (không phải mốc 12h cố định):
      // miễn phí 22h ngày đầu + 24h mỗi ngày tiếp theo. Ở quá ngưỡng đó mới tính thêm 60k/h.
      const freeHours = 22 + 24 * (dayDiff - 1);
      if (duration > freeHours) {
        const extraHours = duration - freeHours;
        price    += extraHours * 60_000;
        parts.push(`${extraHours}h × 60.000đ`);
      }
      result = { price, rateType: parts.join(" + "), shiftLabel: "Ca ngày" };
      break;
    }

    default:
      result = { price: 0, rateType: "Không xác định", shiftLabel: "N/A" };
  }

  return result;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
  }).format(amount);
}
